/**
 * Uploading a file to Fansly (its own three-step flow, as the Fansly
 * workspace's voice notes do it — docs/fansly_crm_behaviour_spec.md §7.6):
 * create an upload, PUT each part to Fansly's storage through the CRM relay
 * (it egresses via the account proxy), complete, then wait until Fansly has
 * processed the file. Falls back to the one-shot multipart route.
 * Resolves with the Fansly media id (a vault file id).
 */
import { FanslyError, type FanslyApi } from './api'
import { asList, asObj, num, str, type Raw } from './data'

export interface UploadProgress {
  /** 0..1 of the bytes sent. */
  fraction: number
  phase: 'uploading' | 'processing'
}

export interface UploadOptions {
  onProgress?: (p: UploadProgress) => void
  signal?: AbortSignal
  /** Waits between processing checks (tests pass 0). */
  pollDelayMs?: (attempt: number) => number
}

interface Part {
  index: number
  url: string
  start: number
  end: number
}

/** The media id an upload answer carries, if any. */
export function uploadedMediaId(payload: unknown): string {
  const root = asObj(payload)
  const bags = [root, asObj(root.media), asObj(root.accountMedia), asObj(asObj(root.accountMedia).media), asObj(root.result), asObj(asObj(root.result).media), ...asList(Array.isArray(payload) ? payload : [])]
  for (const b of bags) {
    const direct = str(b.mediaId) || str(b.media_id)
    if (direct) return direct
  }
  for (const b of bags) {
    if (str(b.id) && (b.locations || b.variants || b.fileSize || b.width != null || /^(audio|video|image)\//i.test(str(b.mimetype || b.mimeType)))) return str(b.id)
  }
  return ''
}

/** The part plan of an upload-create answer: URLs, slice bounds, part size. */
export function partsOf(created: Raw, fileSize: number): Part[] {
  const size = Math.max(1, fileSize)
  const partSize = num(created.partSize ?? created.part_size)
  const out: Array<{ index: number; url: string }> = []
  const push = (url: unknown, index: number) => {
    const href = str(url).trim()
    if (/^https:\/\//i.test(href)) out.push({ index, url: href })
  }
  const list = created.parts ?? created.uploadParts ?? created.locations ?? created.urls
  if (Array.isArray(list)) {
    list.forEach((p, i) => {
      if (typeof p === 'string') push(p, i + 1)
      else {
        const o = asObj(p)
        const idx = Number(o.index ?? o.partNumber ?? o.part_number)
        push(o.uploadUrl ?? o.url ?? o.putUrl ?? o.location ?? o.href, Number.isFinite(idx) ? idx : i + 1)
      }
    })
  }
  if (!out.length) push(created.uploadUrl ?? created.url ?? created.putUrl ?? created.location, 1)
  out.sort((a, b) => a.index - b.index)
  if (out.length === 1) return [{ ...out[0]!, start: 0, end: size - 1 }]
  const chunk = partSize || Math.floor(size / out.length) || size
  let offset = 0
  return out.map((p, i) => {
    const len = i === out.length - 1 ? size - offset : Math.min(size - offset, chunk)
    const part = { ...p, start: offset, end: offset + Math.max(len, 1) - 1 }
    offset += len
    return part
  })
}

const aborted = () => new DOMException('Upload cancelled', 'AbortError')

async function viaCreate(api: FanslyApi, file: File, opts: UploadOptions): Promise<string> {
  const mimeType = file.type || 'application/octet-stream'
  const created = asObj(await api.post('/crm/media/uploads/create', { fileSize: file.size, mimeType, fileName: file.name, uploadFormData: null }))
  const uploadId = str(created.uploadId) || str(created.upload_id) || str(created.id)
  const plan = partsOf(created, file.size)
  if (!uploadId || !plan.length) throw new FanslyError('Fansly did not return an upload for this file.', 502, 'upload_plan_missing')
  const parts: Array<{ index: number; etag: string; eTag: string }> = []
  let sent = 0
  for (const part of plan) {
    if (opts.signal?.aborted) throw aborted()
    const chunk = file.slice(part.start, part.end + 1)
    const etag = await api.relayPut(part.url, chunk)
    parts.push({ index: part.index, etag, eTag: etag })
    sent += chunk.size
    opts.onProgress?.({ fraction: sent / Math.max(1, file.size), phase: 'uploading' })
  }
  const type = Number.isFinite(Number(created.type)) && created.type != null ? Number(created.type) : mimeType.startsWith('video/') ? 2 : mimeType.startsWith('audio/') ? 3 : 1
  const completed = await api.post('/crm/media/uploads/complete', {
    uploadId,
    id: uploadId,
    type,
    partSize: num(created.partSize),
    status: num(created.status),
    waitForComplete: 0,
    parts,
  })
  opts.onProgress?.({ fraction: 1, phase: 'processing' })
  const direct = uploadedMediaId(completed)
  if (direct) return direct
  for (let i = 0; i < 20; i += 1) {
    if (opts.signal?.aborted) throw aborted()
    const status = asObj(await api.get('/crm/media/uploads/status', { uploadId }))
    const id = uploadedMediaId(status) || (num(status.status) >= 6 ? uploadedMediaId(status.media) : '')
    if (id) return id
    if (/^(failed|error)$/i.test(str(status.state || status.phase))) throw new FanslyError(str(status.message) || 'Fansly could not process this file.', 502, 'upload_failed')
    const wait = opts.pollDelayMs ? opts.pollDelayMs(i) : 500 + i * 150
    if (wait) await new Promise((r) => setTimeout(r, wait))
  }
  throw new FanslyError('Fansly is still processing this file. It will appear in the Vault when ready.', 504, 'upload_processing')
}

async function viaMultipart(api: FanslyApi, file: File, opts: UploadOptions): Promise<string> {
  const form = new FormData()
  form.append('metadata', JSON.stringify({ fileName: file.name, mimeType: file.type, fileSize: file.size }))
  form.append('file', file, file.name)
  opts.onProgress?.({ fraction: 0, phase: 'uploading' })
  const payload = await api.upload('/crm/media/upload', form)
  opts.onProgress?.({ fraction: 1, phase: 'processing' })
  const id = uploadedMediaId(payload)
  if (!id) throw new FanslyError('Fansly uploaded the file but did not return it yet. Find it in the Vault in a moment.', 502, 'upload_media_missing')
  return id
}

/** Upload one file; resolves with its Fansly media id. */
export async function uploadToFansly(api: FanslyApi, file: File, opts: UploadOptions = {}): Promise<string> {
  try {
    return await viaCreate(api, file, opts)
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e
    // Some files (and older gateways) only take the one-shot route.
    if (e instanceof FanslyError && (e.code === 'upload_processing' || e.code === 'upload_failed')) throw e
    return viaMultipart(api, file, opts)
  }
}
